import * as React from "react";
import { CalendarDays, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

/*
 * Every date field in the app: typed and shown as dd/mm/yyyy, while the value going in and out stays yyyy-mm-dd (what the server uses), so a page
 * that used <Input type="date"> needs no change. onChange gets an event-like object with target.value = "yyyy-mm-dd" (or "" when cleared), and
 * fires only for a complete, real date inside min / max. The calendar opens under its icon, like the browser's: month title with up/down arrows,
 * Sunday-first weeks, today outlined, the chosen day in blue, Clear and Today, and a month / year jump from the title.
 */
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const pad = (n) => String(n).padStart(2, "0");
export const toIso = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
const todayIso = () => { const t = new Date(); return toIso(t.getFullYear(), t.getMonth() + 1, t.getDate()); };

export function isoToDisplay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

function realDate(y, m, d) {
  if (!(y >= 1000 && y <= 9999 && m >= 1 && m <= 12 && d >= 1)) return false;
  return d <= new Date(y, m, 0).getDate();
}

/** "dd/mm/yyyy" (or a pasted yyyy-mm-dd) to yyyy-mm-dd; null when it is not a whole, real date. */
export function displayToIso(text) {
  const t = (text || "").trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (m) return realDate(+m[1], +m[2], +m[3]) ? t : null;
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(t);
  if (!m) return null;
  return realDate(+m[3], +m[2], +m[1]) ? toIso(+m[3], +m[2], +m[1]) : null;
}

/** Typing digits puts the slashes in: 0211 -> 02/11, 02112026 -> 02/11/2026. */
export function maskTyping(raw) {
  if (/^\d{4}-/.test(raw)) return raw.slice(0, 10);                    // a pasted yyyy-mm-dd is kept as it is
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean);
  return parts.join("/");
}

// width, margin and flex classes belong on the box around the field (so the calendar sits under it); the rest stay on the field
const OUTER = /^(?:[a-z]+:)?(?:w-|min-w-|max-w-|flex-|shrink|grow|basis-|m[trblxy]?-|-m[trblxy]?-|self-|col-|row-|order-|hidden$|block$|inline)/;
export function splitClasses(className) {
  const outer = [], inner = [];
  (className || "").split(/\s+/).filter(Boolean).forEach((c) => (OUTER.test(c) ? outer : inner).push(c));
  return { outer: outer.join(" "), inner: inner.join(" ") };
}

const DateInput = React.forwardRef(function DateInput({ value, onChange, onBlur, min, max, disabled, className, name, id, placeholder, required, readOnly, ...rest }, ref) {
  const { outer, inner } = splitClasses(className);
  const [text, setText] = React.useState(isoToDisplay(value));
  const [open, setOpen] = React.useState(false);
  const [bad, setBad] = React.useState(false);
  const wrap = React.useRef(null);
  React.useEffect(() => { setText(isoToDisplay(value)); setBad(false); }, [value]);
  React.useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);

  const inRange = (iso) => (!min || iso >= min) && (!max || iso <= max);
  const emit = (iso) => onChange?.({ target: { value: iso, name, id }, currentTarget: { value: iso, name, id }, preventDefault() {}, stopPropagation() {} });

  const typed = (e) => {
    const next = maskTyping(e.target.value);
    setText(next);
    if (next === "") { setBad(false); if (value) emit(""); return; }
    const iso = displayToIso(next);
    if (iso && inRange(iso)) { setBad(false); if (iso !== value) emit(iso); }
    else setBad(next.length >= 10);
  };
  const left = (e) => {
    if (text && !displayToIso(text)) { setText(isoToDisplay(value)); setBad(false); }    // an unfinished date goes back to the last good one
    else if (text && !inRange(displayToIso(text))) { setText(isoToDisplay(value)); setBad(false); }
    onBlur?.(e);
  };
  const pick = (iso) => { setText(isoToDisplay(iso)); setBad(false); if (iso !== value) emit(iso); setOpen(false); };

  return (
    <div ref={wrap} className={cn("relative", /(^|\s)(?:[a-z]+:)?(?:w-|flex-1|grow)/.test(outer) ? "" : "w-full", outer)}>
      <input
        ref={ref} id={id} name={name} type="text" inputMode="numeric" autoComplete="off" placeholder={placeholder || "dd/mm/yyyy"} value={text}
        onChange={typed} onBlur={left} disabled={disabled} readOnly={readOnly} required={required} aria-invalid={bad || undefined} maxLength={10}
        className={cn("flex h-9 w-full rounded-md border border-input bg-transparent py-1 pl-3 pr-9 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          bad && "border-rose-500 focus-visible:ring-rose-500", inner)}
        {...rest} />
      <button type="button" tabIndex={-1} aria-label="Open calendar" disabled={disabled || readOnly} onClick={() => setOpen((o) => !o)}
        className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-accent disabled:opacity-50"
        data-testid={rest["data-testid"] ? `${rest["data-testid"]}-calendar-button` : "date-calendar-button"}>
        <CalendarDays className="h-4 w-4" />
      </button>
      {open && <Calendar value={value} min={min} max={max} onPick={pick} onClear={() => pick("")} testId={rest["data-testid"]} />}
    </div>
  );
});

function Calendar({ value, min, max, onPick, onClear, testId }) {
  const start = value || todayIso();
  const [view, setView] = React.useState({ y: +start.slice(0, 4), m: +start.slice(5, 7) });
  const [jumping, setJumping] = React.useState(false);
  const today = todayIso();
  const first = new Date(view.y, view.m - 1, 1).getDay();                 // Sunday = 0: weeks start on Sunday, like the browser's picker
  const days = new Date(view.y, view.m, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const step = (n) => setView(({ y, m }) => { const d = new Date(y, m - 1 + n, 1); return { y: d.getFullYear(), m: d.getMonth() + 1 }; });
  const allowed = (iso) => (!min || iso >= min) && (!max || iso <= max);
  const tid = (s) => (testId ? `${testId}-${s}` : `date-${s}`);
  return (
    <div role="dialog" aria-label="Choose a date" className="absolute left-0 top-full z-50 mt-1 w-64 rounded-md border border-border bg-popover p-2 text-sm text-popover-foreground shadow-lg" data-testid={tid("calendar")}>
      <div className="mb-1 flex items-center justify-between">
        <button type="button" className="rounded px-1.5 py-0.5 font-medium hover:bg-accent" onClick={() => setJumping((j) => !j)} data-testid={tid("calendar-title")}>
          {MONTHS[view.m - 1]} {view.y} <span className="text-xs">▾</span></button>
        <div className="flex">
          <button type="button" aria-label="Previous month" className="rounded p-1 hover:bg-accent" onClick={() => step(-1)} data-testid={tid("calendar-prev")}><ChevronUp className="h-4 w-4" /></button>
          <button type="button" aria-label="Next month" className="rounded p-1 hover:bg-accent" onClick={() => step(1)} data-testid={tid("calendar-next")}><ChevronDown className="h-4 w-4" /></button>
        </div>
      </div>
      {jumping ? (
        <div className="max-h-56 space-y-2 overflow-y-auto" data-testid={tid("calendar-jump")}>
          <div className="flex items-center justify-between px-1">
            <button type="button" className="rounded px-2 hover:bg-accent" onClick={() => setView((v) => ({ ...v, y: v.y - 1 }))} aria-label="Previous year">‹</button>
            <span className="font-medium">{view.y}</span>
            <button type="button" className="rounded px-2 hover:bg-accent" onClick={() => setView((v) => ({ ...v, y: v.y + 1 }))} aria-label="Next year">›</button>
          </div>
          <div className="grid grid-cols-3 gap-1">{MONTHS.map((name, i) => (
            <button key={name} type="button" className={cn("rounded py-1 hover:bg-accent", i + 1 === view.m && "bg-blue-600 text-white hover:bg-blue-600")}
              onClick={() => { setView((v) => ({ ...v, m: i + 1 })); setJumping(false); }}>{name.slice(0, 3)}</button>))}</div>
        </div>
      ) : (<>
        <div className="grid grid-cols-7 text-center text-[11px] text-muted-foreground">{["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <div key={d} className="py-1">{d}</div>)}</div>
        <div className="grid grid-cols-7 gap-0.5 text-center">{cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />;
          const iso = toIso(view.y, view.m, d);
          const chosen = iso === value;
          return (
            <button key={iso} type="button" disabled={!allowed(iso)} onClick={() => onPick(iso)} data-testid={tid(`day-${iso}`)} aria-pressed={chosen}
              className={cn("h-7 rounded text-xs hover:bg-accent disabled:cursor-not-allowed disabled:opacity-30",
                iso === today && !chosen && "ring-1 ring-foreground/60", chosen && "bg-blue-600 font-semibold text-white hover:bg-blue-600")}>{d}</button>);
        })}</div>
      </>)}
      <div className="mt-2 flex justify-between border-t border-border pt-2">
        <button type="button" className="text-xs text-blue-600 hover:underline" onClick={onClear} data-testid={tid("calendar-clear")}>Clear</button>
        <button type="button" className="text-xs text-blue-600 hover:underline disabled:opacity-40" disabled={!allowed(today)} onClick={() => onPick(today)} data-testid={tid("calendar-today")}>Today</button>
      </div>
    </div>
  );
}

export { DateInput };
